import { Config } from '../config';

/**
 * Offensive Operations WebSocket Service
 * Handles real-time communication for offensive security operations
 */
class OffensiveWebSocketService {
  constructor() {
    this.ws = null;
    this.reconnectTimeout = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 5000; // 5 seconds
    this.eventHandlers = new Map();
    this.isConnecting = false;
  }

  /**
   * Connect to WebSocket for specific engagement
   * @param {string} engagementId - Engagement ID
   * @param {Object} options - Connection options
   * @returns {Promise<void>}
   */
  async connect(engagementId, options = {}) {
    if (this.isConnecting) {
      console.log('WebSocket connection already in progress');
      return;
    }

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('WebSocket already connected');
      return;
    }

    this.isConnecting = true;
    this.engagementId = engagementId;

    try {
      // Convert HTTP URL to WebSocket URL
      // Remove /api from the path if present, as WebSocket endpoints are typically at root level
      const wsProtocol = Config.APIBaseURL.startsWith('https') ? 'wss' : 'ws';
      let wsUrl = Config.APIBaseURL.replace(/^https?/, wsProtocol);
      // Remove /api from the URL if it exists
      wsUrl = wsUrl.replace(/\/api$/, '');
      const url = `${wsUrl}/ws/offensive/${engagementId}`;

      console.log('Connecting to WebSocket:', url);

      this.ws = new WebSocket(url);

      this.ws.onopen = () => {
        console.log('WebSocket connected');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        
        // Send authentication token
        const token = localStorage.getItem('token');
        if (token) {
          this.send({
            type: 'auth',
            token: token
          });
        }

        // Emit connection event
        this.emit('connected', { engagementId });
      };

      this.ws.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);
          console.log('WebSocket message received:', message);
          
          // Emit the event to registered handlers
          this.emit(message.type, message.data || message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          this.emit('error', { type: 'parse_error', error: error.message });
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.emit('error', { type: 'connection_error', error });
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket disconnected:', event.code, event.reason);
        this.isConnecting = false;
        this.emit('disconnected', { code: event.code, reason: event.reason });
        
        // Attempt to reconnect if not a manual close
        if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
          this.scheduleReconnect();
        }
      };

    } catch (error) {
      console.error('Error creating WebSocket connection:', error);
      this.isConnecting = false;
      this.emit('error', { type: 'connection_failed', error: error.message });
    }
  }

  /**
   * Disconnect from WebSocket
   */
  disconnect() {
    if (this.reconnectTimeout) {
      clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }

    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }

    this.isConnecting = false;
    this.reconnectAttempts = 0;
  }

  /**
   * Send message through WebSocket
   * @param {Object} message - Message to send
   * @returns {boolean} Success status
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      try {
        this.ws.send(JSON.stringify(message));
        return true;
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
        this.emit('error', { type: 'send_error', error: error.message });
        return false;
      }
    } else {
      console.warn('WebSocket is not connected. Message not sent:', message);
      return false;
    }
  }

  /**
   * Register event handler
   * @param {string} eventType - Event type to listen for
   * @param {Function} handler - Event handler function
   * @returns {Function} Unsubscribe function
   */
  on(eventType, handler) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, new Set());
    }
    
    this.eventHandlers.get(eventType).add(handler);
    
    // Return unsubscribe function
    return () => {
      const handlers = this.eventHandlers.get(eventType);
      if (handlers) {
        handlers.delete(handler);
        if (handlers.size === 0) {
          this.eventHandlers.delete(eventType);
        }
      }
    };
  }

  /**
   * Emit event to all registered handlers
   * @param {string} eventType - Event type
   * @param {*} data - Event data
   */
  emit(eventType, data) {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      handlers.forEach(handler => {
        try {
          handler(data);
        } catch (error) {
          console.error(`Error in event handler for ${eventType}:`, error);
        }
      });
    }
  }

  /**
   * Schedule reconnection attempt
   */
  scheduleReconnect() {
    if (this.reconnectTimeout) {
      return; // Already scheduled
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1); // Exponential backoff
    
    console.log(`Scheduling reconnection attempt ${this.reconnectAttempts} in ${delay}ms`);
    
    this.reconnectTimeout = setTimeout(() => {
      this.reconnectTimeout = null;
      if (this.engagementId) {
        this.connect(this.engagementId);
      }
    }, delay);
  }

  /**
   * Get connection status
   * @returns {Object} Connection status
   */
  getStatus() {
    return {
      connected: this.ws && this.ws.readyState === WebSocket.OPEN,
      connecting: this.isConnecting,
      reconnectAttempts: this.reconnectAttempts,
      engagementId: this.engagementId
    };
  }

  // ==================== SPECIFIC OFFENSIVE OPERATIONS ====================

  /**
   * Request technique execution
   * @param {string} techniqueId - Technique ID
   * @param {Object} parameters - Execution parameters
   */
  requestTechniqueExecution(techniqueId, parameters = {}) {
    return this.send({
      type: 'execute_technique',
      technique_id: techniqueId,
      parameters
    });
  }

  /**
   * Request technique stop
   * @param {string} techniqueId - Technique ID
   */
  requestTechniqueStop(techniqueId) {
    return this.send({
      type: 'stop_technique',
      technique_id: techniqueId
    });
  }

  /**
   * Request engagement control
   * @param {string} action - Action to perform (start, pause, resume, stop)
   */
  requestEngagementControl(action) {
    return this.send({
      type: 'engagement_control',
      action
    });
  }

  /**
   * Request tactic progression
   * @param {string} tacticId - Tactic ID to progress to
   */
  requestTacticProgression(tacticId) {
    return this.send({
      type: 'progress_tactic',
      tactic_id: tacticId
    });
  }

  /**
   * Subscribe to technique output
   * @param {string} techniqueId - Technique ID
   */
  subscribeToTechniqueOutput(techniqueId) {
    return this.send({
      type: 'subscribe_output',
      technique_id: techniqueId
    });
  }

  /**
   * Unsubscribe from technique output
   * @param {string} techniqueId - Technique ID
   */
  unsubscribeFromTechniqueOutput(techniqueId) {
    return this.send({
      type: 'unsubscribe_output',
      technique_id: techniqueId
    });
  }

  /**
   * Request engagement status update
   */
  requestStatusUpdate() {
    return this.send({
      type: 'status_update'
    });
  }

  /**
   * Send heartbeat to keep connection alive
   */
  sendHeartbeat() {
    return this.send({
      type: 'heartbeat',
      timestamp: Date.now()
    });
  }
}

// Create and export singleton instance
const offensiveWebSocket = new OffensiveWebSocketService();
export default offensiveWebSocket;
